import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ReportForList } from '@/types/reports';
import type { ReportSort, ReportSortKey } from '../types';
import { useAuth } from '@/contexts/auth';
import { canOperateReports } from '@/utils/permissions';
import { ReportsTableSkeleton } from './ReportsTableSkeleton';
import { ReportsTableRow } from './ReportsTableRow';
import { ReportsSortableHeader } from './ReportsSortableHeader';

interface ReportsTableProps {
  reports: ReportForList[];
  loading: boolean;
  error: boolean;
  itemsPerPage: number;
  copyingReportId: string | null;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onOpenPreview: (id: string) => void;
  onCopy: (id: string) => void;
  onDelete: (id: string, title: string) => void;
  onStartSend: (id: string, title: string) => void;
  onRetry?: () => void;
  sort: ReportSort | null;
  onSort: (_key: ReportSortKey) => void;
}

export const ReportsTable: React.FC<ReportsTableProps> = ({
  reports,
  loading,
  error,
  itemsPerPage,
  copyingReportId,
  onEdit,
  onOpenPreview,
  onCopy,
  onDelete,
  onStartSend,
  onRetry,
  sort,
  onSort,
}) => {
  const { user } = useAuth();
  const showActions = canOperateReports(user);
  const columnCount = showActions ? 9 : 8;

  return (
    <div className="mb-6 w-full min-w-0 max-w-full overflow-x-auto rounded-xl border border-gray-200 [&>div]:max-w-full [&>div]:overflow-x-auto">
      <Table className="w-max min-w-full">
        <colgroup>
          <col className="min-w-[220px]" />
          <col className="min-w-[72px]" />
          <col className="min-w-[120px]" />
          <col className="min-w-[140px]" />
          <col className="min-w-[120px]" />
          <col className="min-w-[100px]" />
          <col className="min-w-[120px]" />
          <col className="min-w-[160px]" />
          {showActions ? <col className="min-w-[80px]" /> : null}
        </colgroup>
        <TableHeader>
          <TableRow className="border-gray-200 hover:bg-transparent">
            <ReportsSortableHeader
              label="Nome do report"
              sortKey="title"
              sort={sort}
              onSort={onSort}
            />
            <TableHead className="h-12 whitespace-nowrap px-4 text-center font-medium text-gray-500">
              Edição
            </TableHead>
            <ReportsSortableHeader
              label="Data de criação"
              sortKey="createdAt"
              sort={sort}
              onSort={onSort}
              className="whitespace-nowrap"
            />
            <ReportsSortableHeader
              label="Data de atualização"
              sortKey="updatedAt"
              sort={sort}
              onSort={onSort}
              className="whitespace-nowrap"
            />
            <ReportsSortableHeader
              label="Marca"
              sortKey="partner_name"
              sort={sort}
              onSort={onSort}
              align="center"
              className="whitespace-nowrap"
            />
            <ReportsSortableHeader
              label="Visitantes"
              sortKey="visitor_count"
              sort={sort}
              onSort={onSort}
              align="center"
              className="whitespace-nowrap"
            />
            <ReportsSortableHeader
              label="Visualizações"
              sortKey="view_count"
              sort={sort}
              onSort={onSort}
              align="center"
              className="whitespace-nowrap"
            />
            <ReportsSortableHeader
              label="Status do envio"
              sortKey="status"
              sort={sort}
              onSort={onSort}
              align="center"
              className="whitespace-nowrap"
            />
            {showActions ? (
              <TableHead className="h-12 whitespace-nowrap px-4 text-center font-medium text-gray-500">
                Ações
              </TableHead>
            ) : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <ReportsTableSkeleton rowCount={Math.min(itemsPerPage, 10)} showActions={showActions} />
          ) : error ? (
            <TableRow>
              <TableCell colSpan={columnCount} className="p-8 text-center">
                <p className="mb-1 font-medium text-gray-700">Erro ao carregar relatórios</p>
                <p className="mb-4 text-sm text-gray-500">
                  Não foi possível buscar os reports. Tente novamente.
                </p>
                {onRetry && (
                  <button
                    type="button"
                    onClick={onRetry}
                    className="text-sm font-medium text-gray-900 underline underline-offset-2 hover:text-gray-700"
                  >
                    Tentar novamente
                  </button>
                )}
              </TableCell>
            </TableRow>
          ) : reports.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columnCount} className="p-8 text-center text-gray-500">
                Nenhum report encontrado
              </TableCell>
            </TableRow>
          ) : (
            reports.map((report) => (
              <ReportsTableRow
                key={report.id}
                report={report}
                isCopying={copyingReportId === report.id}
                showActions={showActions}
                onEdit={onEdit}
                onOpenPreview={onOpenPreview}
                onCopy={onCopy}
                onDelete={onDelete}
                onStartSend={onStartSend}
              />
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};
